import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Mail, Phone, Building2, Package, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gfe_admin_token";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.access_token);
      toast.success("Welcome back, admin.");
      onLogin(res.data.access_token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const field = "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-slate-400 focus:outline-none text-sm text-slate-900 placeholder-slate-400";

  return (
    <main className="min-h-[70vh] flex items-center justify-center px-4" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-md p-8 rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="text-xs uppercase tracking-widest text-slate-500">Admin</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">Green Fuse Console</h1>
        <p className="text-sm text-slate-500 mt-2">Sign in to manage incoming quote requests.</p>
        <div className="mt-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 block">Email</label>
            <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} placeholder="admin@greenfuseenergy.com" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 block">Password</label>
            <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={field} placeholder="••••••••" />
          </div>
          <button data-testid="admin-login-btn" disabled={loading} className="btn-orange w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </div>
      </form>
    </main>
  );
}

function StatusPill({ status }) {
  const map = {
    new: "bg-blue-50 text-blue-700 border-blue-200",
    in_progress: "bg-amber-50 text-amber-700 border-amber-200",
    closed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${map[status] || map.new}`}>{status.replace("_", " ")}</span>;
}

function Dashboard({ token, onLogout }) {
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, in_progress: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const [qs, st] = await Promise.all([
        axios.get(`${API}/admin/quotes`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setQuotes(qs.data || []);
      setStats(st.data || stats);
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
      } else {
        toast.error("Failed to load quotes.");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/quotes/${id}`, { status }, { headers });
      toast.success("Status updated");
      setSelected((s) => (s && s.id === id ? { ...s, status } : s));
      load();
    } catch { toast.error("Failed to update"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this quote request?")) return;
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, { headers });
      toast.success("Deleted");
      if (selected?.id === id) setSelected(null);
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const filtered = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

  return (
    <main data-testid="admin-dashboard" className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8 py-10 md:py-14">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Admin Console</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold mt-1">Quote Requests</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="admin-refresh" onClick={load} className="btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-slate-200 text-slate-700 hover:border-slate-400">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
        {[
          { label: "Total", value: stats.total, k: "all" },
          { label: "New", value: stats.new, k: "new" },
          { label: "In Progress", value: stats.in_progress, k: "in_progress" },
          { label: "Closed", value: stats.closed, k: "closed" },
        ].map((s) => (
          <button
            key={s.k}
            data-testid={`stat-${s.k}`}
            onClick={() => setFilter(s.k)}
            className={`p-5 rounded-2xl border text-left transition ${filter === s.k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
          >
            <div className={`text-xs uppercase tracking-widest ${filter === s.k ? "text-slate-300" : "text-slate-500"}`}>{s.label}</div>
            <div className="font-display text-3xl font-extrabold mt-1">{s.value}</div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7">
          <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
            <div className="max-h-[640px] overflow-auto">
              {loading ? (
                <div className="p-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="p-10 text-center text-slate-500" data-testid="admin-empty">No quote requests yet.</div>
              ) : filtered.map((q) => (
                <button key={q.id} onClick={() => setSelected(q)} data-testid={`quote-row-${q.id}`}
                  className={`w-full text-left p-5 border-b border-slate-100 hover:bg-slate-50 transition ${selected?.id === q.id ? "bg-slate-50" : ""}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-display font-bold text-slate-900">{q.name} <span className="text-slate-400 font-normal text-sm">· {q.company || "—"}</span></div>
                      <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                        <span className="flex items-center gap-1"><Package className="w-3.5 h-3.5" /> {q.category}</span>
                        <span>{new Date(q.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <StatusPill status={q.status || "new"} />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-5">
          {selected ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="quote-detail">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Quote #{selected.id.slice(0, 8)}</div>
                  <h3 className="font-display text-xl font-bold mt-1">{selected.name}</h3>
                </div>
                <StatusPill status={selected.status || "new"} />
              </div>
              <div className="mt-4 space-y-2 text-sm">
                {selected.company && <div className="flex items-center gap-2 text-slate-700"><Building2 className="w-4 h-4 text-slate-400" /> {selected.company}</div>}
                <div className="flex items-center gap-2 text-slate-700"><Mail className="w-4 h-4 text-slate-400" /> <a className="hover:underline" href={`mailto:${selected.email}`}>{selected.email}</a></div>
                <div className="flex items-center gap-2 text-slate-700"><Phone className="w-4 h-4 text-slate-400" /> <a className="hover:underline" href={`tel:${selected.phone}`}>{selected.phone}</a></div>
                <div className="flex items-center gap-2 text-slate-700"><Package className="w-4 h-4 text-slate-400" /> {selected.category}</div>
              </div>
              <div className="mt-5 border-t border-slate-100 pt-4">
                <div className="text-xs uppercase tracking-widest text-slate-500">Product Details</div>
                <p className="text-sm text-slate-800 mt-1 whitespace-pre-line">{selected.product_details}</p>
              </div>
              {(selected.quantity || selected.message) && (
                <div className="mt-4 grid grid-cols-2 gap-4">
                  {selected.quantity && (
                    <div>
                      <div className="text-xs uppercase tracking-widest text-slate-500">Quantity</div>
                      <div className="text-sm text-slate-800 mt-1">{selected.quantity}</div>
                    </div>
                  )}
                  {selected.message && (
                    <div>
                      <div className="text-xs uppercase tracking-widest text-slate-500">Delivery</div>
                      <div className="text-sm text-slate-800 mt-1">{selected.message}</div>
                    </div>
                  )}
                </div>
              )}
              <div className="mt-5 text-xs text-slate-500">
                Email sent to sales: <b className={selected.email_sent ? "text-emerald-600" : "text-amber-600"}>{selected.email_sent ? "Yes" : "Pending"}</b>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                <select data-testid="quote-status-select" value={selected.status || "new"} onChange={(e) => updateStatus(selected.id, e.target.value)}
                  className="px-3 py-2 rounded-full border border-slate-200 text-sm">
                  <option value="new">New</option>
                  <option value="in_progress">In Progress</option>
                  <option value="closed">Closed</option>
                </select>
                <a href={`mailto:${selected.email}?subject=Re: Quote for ${selected.category}`} className="btn-orange inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold">
                  <Mail className="w-4 h-4" /> Reply
                </a>
                <button onClick={() => del(selected.id)} data-testid="quote-delete-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500 bg-slate-50" data-testid="quote-detail-empty">
              Select a quote request to view details.
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <Login onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
